
import React, { useState } from 'react';
import { MainNav } from '@/components/MainNav';
import { PageSEO } from '@/components/PageSEO';
import { PredictionsList } from '@/components/predictions/PredictionsList';
import { useHydratedPredictions } from '@/hooks/predictions/useHydratedPredictions';
import { triggerPredictionsWebhook } from '@/services/predictions/webhook';
import { useRoleCheck } from '@/hooks/useRoleCheck';

interface ServerPredictionsPageProps {
  initialPredictions?: any[];
}

function ServerPredictionsPage({ initialPredictions = [] }: ServerPredictionsPageProps) {
  const { 
    predictions, 
    loading, 
    error,
    retryLoading 
  } = useHydratedPredictions({ initialPredictions });
  
  const { isAdmin, loading: roleLoading } = useRoleCheck();
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      console.log('[ServerPredictionsPage] Triggering webhook...');
      await triggerPredictionsWebhook();
      
      // Wait a bit and then reload predictions
      setTimeout(async () => {
        try {
          console.log('[ServerPredictionsPage] Reloading predictions after generation...');
          retryLoading();
        } catch (error) {
          console.error('[ServerPredictionsPage] Error reloading predictions:', error);
        } finally {
          setIsGenerating(false);
        }
      }, 3000);
    } catch (error) {
      console.error('[ServerPredictionsPage] Error generating predictions:', error);
      setIsGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <PageSEO 
          title="Previsioni AI Metalli Preziosi | Gildy"
          description="Analisi predittive basate su intelligenza artificiale per oro e argento. Scopri le previsioni di mercato per i metalli preziosi."
          canonicalPath="/previsioni"
          keywords={["previsioni ai", "metalli preziosi", "oro argento", "analisi predittive", "intelligenza artificiale"]}
        />
        
        <MainNav />
        <main className="flex-1 container mx-auto py-8 px-4 md:px-6" role="main">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Previsioni AI Metalli Preziosi
            </h1>
            <p className="text-muted-foreground">
              Analisi predittive basate su intelligenza artificiale per oro e argento
            </p>
          </div>
          <div className="text-center py-8">
            <p className="text-muted-foreground">Caricamento previsioni...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PageSEO 
        title="Previsioni AI Metalli Preziosi | Gildy"
        description="Analisi predittive basate su intelligenza artificiale per oro e argento. Scopri le previsioni di mercato per i metalli preziosi."
        canonicalPath="/previsioni"
        keywords={["previsioni ai", "metalli preziosi", "oro argento", "analisi predittive", "intelligenza artificiale"]}
      />
      
      <MainNav />
      <main className="flex-1 container mx-auto py-8 px-4 md:px-6" role="main">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Previsioni AI Metalli Preziosi
          </h1>
          <p className="text-muted-foreground">
            Analisi predittive basate su intelligenza artificiale per oro e argento
          </p>
        </div>

        <PredictionsList 
          predictions={predictions}
          markdownContent={null}
          isGenerating={isGenerating}
          canGenerate={!isGenerating && isAdmin && !roleLoading}
          onGenerate={handleGenerate}
        />
      </main>
    </div>
  );
}

export default ServerPredictionsPage;
